import { FileSpreadsheet, Loader2 } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import type { JsExcelPreview } from "@js-preview/excel";
import { cn } from "@/modern/utils";
import { assertSafeSpreadsheetPayload, preventUnsafePreviewNavigation, rewriteMouseOffsetRelativeTo } from "./excel-preview-safety";
import "@js-preview/excel/lib/index.css";
import "./excel-viewer.css";

export interface ExcelViewerProps {
  fileUrl: string;
  fileName?: string;
  showName?: boolean;
  className?: string;
  onLoadSuccess?: () => void;
  onLoadError?: (error: Error) => void;
  isThumbnail?: boolean;
}

/** x-spreadsheet subtracts this from view height when the sheet-tab bar is enabled. */
const X_SPREADSHEET_BOTTOM_BAR_PX = 41;
const X_SPREADSHEET_COL_PX = 80;
const X_SPREADSHEET_INDEX_COL_PX = 60;
const X_SPREADSHEET_ROW_PX = 24;
const X_SPREADSHEET_HEADER_ROW_PX = 25;
/** Extra columns past the viewport so used-range sheets can pan like Excel. */
const X_SPREADSHEET_OVERFLOW_COLS = 8;
const X_SPREADSHEET_OVERFLOW_ROWS = 8;
const X_SPREADSHEET_CELL_PAD_PX = 16;
const X_SPREADSHEET_MAX_COL_PX = 520;
const X_SPREADSHEET_FIT_ROW_LIMIT = 400;
const X_SPREADSHEET_MEASURE_FONT = "13px Arial";

type PreviewCell = {
  text?: unknown;
  value?: unknown;
  formula?: unknown;
} | null;

type SheetColumns = {
  len: number;
  getWidth: (columnIndex: number) => number;
  setWidth: (columnIndex: number, width: number) => void;
};

type SheetRows = {
  len: number;
  getCell: (rowIndex: number, columnIndex: number) => PreviewCell;
};

type SpreadsheetScrollbar = {
  move: (pos: { left?: number; top?: number }) => void;
  scroll: () => { left: number; top: number };
};

type SpreadsheetSheet = {
  reload: () => void;
  data?: { cols?: SheetColumns; rows?: SheetRows };
  horizontalScrollbar?: SpreadsheetScrollbar;
};

type SpreadsheetBottomBar = {
  swapFunc: (sheetIndex: number) => void;
};

type ExcelPreviewInternals = JsExcelPreview & {
  xs?: { sheet?: SpreadsheetSheet; bottombar?: SpreadsheetBottomBar };
};

type SelectedCellInfo = {
  address: string;
  text: string;
};

const minColsToFillWidth = (width: number): number =>
  Math.max(
    26,
    Math.ceil((Math.max(width, 0) - X_SPREADSHEET_INDEX_COL_PX) / X_SPREADSHEET_COL_PX) + X_SPREADSHEET_OVERFLOW_COLS,
  );

const minRowsToFillHeight = (height: number): number =>
  Math.max(
    100,
    Math.ceil((Math.max(height, 0) - X_SPREADSHEET_HEADER_ROW_PX) / X_SPREADSHEET_ROW_PX) + X_SPREADSHEET_OVERFLOW_ROWS,
  );

const columnAddress = (columnIndex: number): string => {
  let n = columnIndex + 1;
  let label = "";
  while (n > 0) {
    const rem = (n - 1) % 26;
    label = String.fromCharCode(65 + rem) + label;
    n = Math.floor((n - 1) / 26);
  }
  return label;
};

/** Formula cells often store the computed result as a number on `text`, not a string. */
const stringifyCellPart = (value: unknown): string => {
  if (value == null || value === "") return "";
  if (typeof value === "string") return value;
  if (typeof value === "number") return Number.isFinite(value) ? String(value) : "";
  if (typeof value === "boolean") return String(value);
  if (Array.isArray(value)) return value.map((part) => stringifyCellPart(part)).join("");
  if (typeof value === "object") {
    const record = value as { error?: unknown; result?: unknown; text?: unknown };
    if (record.error != null && record.error !== "") return stringifyCellPart(record.error);
    if (record.result != null && record.result !== "") return stringifyCellPart(record.result);
    if (record.text != null && record.text !== "") return stringifyCellPart(record.text);
  }
  return "";
};

const cellDisplayText = (cell: PreviewCell): string => {
  if (cell == null) return "";
  return stringifyCellPart(cell.text) || stringifyCellPart(cell.value) || stringifyCellPart(cell.formula);
};

const measureCellTextWidth = (text: string, context: CanvasRenderingContext2D | null): number => {
  const lines = text.split(/\r?\n/);
  let widest = 0;
  for (const line of lines) {
    if (line.length === 0) continue;
    const width = context ? context.measureText(line).width : line.length * 8;
    if (width > widest) widest = width;
  }
  return widest;
};

const pinHostToSize = (el: HTMLElement, width: number, height: number, clipSheetTabBar: boolean) => {
  if (width > 0 && el.style.width !== `${width}px`) el.style.width = `${width}px`;
  if (height > 0) {
    const pinnedHeight = `${height + (clipSheetTabBar ? X_SPREADSHEET_BOTTOM_BAR_PX : 0)}px`;
    if (el.style.height !== pinnedHeight) el.style.height = pinnedHeight;
  }
};

const eventTargetIsSheetTabBar = (target: EventTarget | null) =>
  target instanceof Element && Boolean(target.closest(".x-spreadsheet-bottombar"));

/** @js-preview/excel logs the parsed workbook on every preview (`console.log(model, "workbook")`). */
const previewWithoutWorkbookLog = async (previewer: ExcelPreviewInternals, data: ArrayBuffer) => {
  const { log } = console;
  console.log = (...args: unknown[]) => {
    if (args[1] === "workbook") return;
    log.apply(console, args as Parameters<typeof console.log>);
  };
  try {
    await previewer.preview(data);
  } finally {
    console.log = log;
  }
};

const fitSheetColumnsToContent = (sheet: SpreadsheetSheet | undefined) => {
  const cols = sheet?.data?.cols;
  const rows = sheet?.data?.rows;
  if (!cols || !rows) return;
  const context = document.createElement("canvas").getContext("2d");
  if (context) context.font = X_SPREADSHEET_MEASURE_FONT;
  const rowLimit = Math.min(rows.len, X_SPREADSHEET_FIT_ROW_LIMIT);
  let changed = false;
  for (let columnIndex = 0; columnIndex < cols.len; columnIndex += 1) {
    const currentWidth = cols.getWidth(columnIndex);
    if (currentWidth < 1) continue;
    let neededWidth = currentWidth;
    for (let rowIndex = 0; rowIndex < rowLimit; rowIndex += 1) {
      const text = cellDisplayText(rows.getCell(rowIndex, columnIndex));
      if (text.length === 0) continue;
      const measured = measureCellTextWidth(text, context) + X_SPREADSHEET_CELL_PAD_PX;
      if (measured > neededWidth) neededWidth = measured;
    }
    const nextWidth = Math.min(X_SPREADSHEET_MAX_COL_PX, neededWidth);
    if (nextWidth === currentWidth) continue;
    cols.setWidth(columnIndex, nextWidth);
    changed = true;
  }
  if (changed) sheet?.reload();
};

const ExcelViewer = ({
  fileUrl,
  fileName,
  showName = false,
  className = "",
  onLoadSuccess,
  onLoadError,
  isThumbnail = false,
}: ExcelViewerProps) => {
  const [excelError, setExcelError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedCell, setSelectedCell] = useState<SelectedCellInfo | null>(null);
  const previewContainerRef = useRef<HTMLDivElement>(null);
  const onLoadSuccessRef = useRef(onLoadSuccess);
  const onLoadErrorRef = useRef(onLoadError);

  onLoadSuccessRef.current = onLoadSuccess;
  onLoadErrorRef.current = onLoadError;

  useEffect(() => {
    let cancelled = false;
    let previewer: ExcelPreviewInternals | null = null;
    let sizeWaitObserver: ResizeObserver | null = null;
    let layoutObserver: ResizeObserver | null = null;
    let sizeWaitTimeout: number | undefined;
    let layoutFrame = 0;
    let lastLayoutKey = "";
    let clickRoot: HTMLElement | null = null;
    let overlayEl: HTMLElement | null = null;
    let sheetPointerActive = false;
    const abortController = new AbortController();

    const waitForContainerSize = (el: HTMLElement) =>
      new Promise<void>((resolve) => {
        if (el.clientWidth > 0 && el.clientHeight > 0) {
          resolve();
          return;
        }
        const finish = () => {
          sizeWaitObserver?.disconnect();
          sizeWaitObserver = null;
          if (sizeWaitTimeout != null) {
            window.clearTimeout(sizeWaitTimeout);
            sizeWaitTimeout = undefined;
          }
          resolve();
        };
        sizeWaitObserver = new ResizeObserver(() => {
          if (el.clientWidth > 0 && el.clientHeight > 0) finish();
        });
        sizeWaitObserver.observe(el);
        sizeWaitTimeout = window.setTimeout(finish, 800);
      });

    const pinPreviewViewport = () => {
      const el = previewContainerRef.current;
      const parent = el?.parentElement;
      if (!el || !parent) return;
      pinHostToSize(el, parent.clientWidth, parent.clientHeight, false);
    };

    const getOverlay = (): HTMLElement | null => {
      if (overlayEl?.isConnected) return overlayEl;
      const next = clickRoot?.querySelector(".x-spreadsheet-overlayer");
      overlayEl = next instanceof HTMLElement ? next : null;
      return overlayEl;
    };

    const alignOverlayToCanvas = () => {
      const el = previewContainerRef.current;
      if (!el) return;
      const canvas = el.querySelector("canvas.x-spreadsheet-table");
      const overlay = getOverlay();
      if (!(canvas instanceof HTMLElement) || !overlay) return;
      const width = `${canvas.clientWidth}px`;
      const height = `${canvas.clientHeight}px`;
      if (overlay.style.width !== width) overlay.style.width = width;
      if (overlay.style.height !== height) overlay.style.height = height;
    };

    const syncSheetLayout = () => {
      const el = previewContainerRef.current;
      const sheet = previewer?.xs?.sheet;
      if (!el || !sheet) return;
      pinPreviewViewport();
      const key = `${el.clientWidth}x${el.clientHeight}`;
      if (key !== lastLayoutKey) {
        lastLayoutKey = key;
        sheet.reload();
      }
      alignOverlayToCanvas();
    };

    const patchMouseOffset = (event: Event) => {
      if (!(event instanceof MouseEvent) || !clickRoot) return;
      if (eventTargetIsSheetTabBar(event.target)) return;
      if (!sheetPointerActive && !clickRoot.contains(event.target as Node)) return;
      const overlay = getOverlay();
      if (!overlay) return;
      rewriteMouseOffsetRelativeTo(overlay, event);
    };

    const onSheetPointerDown = (event: MouseEvent) => {
      if (!clickRoot?.contains(event.target as Node)) return;
      if (eventTargetIsSheetTabBar(event.target)) return;
      sheetPointerActive = true;
      patchMouseOffset(event);
    };

    const onWindowPointerUp = () => {
      sheetPointerActive = false;
    };

    const onSheetWheel = (event: Event) => {
      if (!(event instanceof WheelEvent)) return;
      if (eventTargetIsSheetTabBar(event.target)) return;
      const sheet = previewer?.xs?.sheet;
      const horizontalBar = sheet?.horizontalScrollbar;
      if (!horizontalBar) return;
      const absX = Math.abs(event.deltaX);
      const absY = Math.abs(event.deltaY);
      const useHorizontal = event.shiftKey || absX > absY;
      if (!useHorizontal) return;
      event.preventDefault();
      event.stopPropagation();
      const { left } = horizontalBar.scroll();
      const delta = event.shiftKey && absX < 1 ? event.deltaY : event.deltaX;
      horizontalBar.move({ left: left + delta });
    };

    const handleCellSelected = ({
      cell,
      rowIndex,
      columnIndex,
    }: {
      cell?: PreviewCell;
      rowIndex: number;
      columnIndex: number;
    }) => {
      if (cancelled) return;
      setSelectedCell({
        address: `${columnAddress(columnIndex)}${rowIndex + 1}`,
        text: cellDisplayText(cell ?? previewer?.xs?.sheet?.data?.rows?.getCell(rowIndex, columnIndex) ?? null),
      });
    };

    const loadWorkbook = async () => {
      if (!previewContainerRef.current || !fileUrl) return;

      try {
        setIsLoading(true);
        setExcelError(null);
        setSelectedCell(null);
        previewContainerRef.current.innerHTML = "";

        const payloadPromise = fetch(fileUrl, { credentials: "include", signal: abortController.signal }).then(
          async (response) => {
            if (!response.ok) {
              throw new Error(`Failed to fetch spreadsheet: ${response.status} ${response.statusText}`);
            }
            const arrayBuffer = await response.arrayBuffer();
            assertSafeSpreadsheetPayload(new Uint8Array(arrayBuffer));
            return arrayBuffer;
          },
        );
        const importPromise = import("@js-preview/excel");
        const [arrayBuffer, { default: jsPreviewExcel }] = await Promise.all([
          payloadPromise,
          importPromise,
          waitForContainerSize(previewContainerRef.current),
        ]).then(([buffer, excelModule]) => [buffer, excelModule] as const);
        if (cancelled || !previewContainerRef.current) return;

        if (isThumbnail) {
          pinHostToSize(
            previewContainerRef.current,
            previewContainerRef.current.clientWidth,
            previewContainerRef.current.clientHeight,
            true,
          );
        } else {
          pinPreviewViewport();
        }

        previewer = jsPreviewExcel.init(previewContainerRef.current, {
          minColLength: minColsToFillWidth(previewContainerRef.current.clientWidth),
          minRowLength: minRowsToFillHeight(previewContainerRef.current.clientHeight),
          showContextmenu: false,
          ...(!isThumbnail ? { cellSelected: handleCellSelected } : {}),
        } as Parameters<typeof jsPreviewExcel.init>[1]);
        await previewWithoutWorkbookLog(previewer, arrayBuffer);
        if (cancelled) return;
        if (!isThumbnail) {
          fitSheetColumnsToContent(previewer.xs?.sheet);
          const bottomBar = previewer.xs?.bottombar;
          if (bottomBar) {
            const previousSwap = bottomBar.swapFunc;
            bottomBar.swapFunc = (sheetIndex: number) => {
              previousSwap.call(bottomBar, sheetIndex);
              overlayEl = null;
              setSelectedCell(null);
              window.setTimeout(() => {
                if (cancelled) return;
                fitSheetColumnsToContent(previewer?.xs?.sheet);
                alignOverlayToCanvas();
              }, 0);
            };
          }
        }

        lastLayoutKey = `${previewContainerRef.current.clientWidth}x${previewContainerRef.current.clientHeight}`;
        clickRoot = previewContainerRef.current;
        clickRoot.addEventListener("click", preventUnsafePreviewNavigation, true);
        clickRoot.addEventListener("auxclick", preventUnsafePreviewNavigation, true);

        if (!isThumbnail) {
          getOverlay();
          clickRoot.addEventListener("mousedown", onSheetPointerDown, true);
          clickRoot.addEventListener("mousemove", patchMouseOffset, true);
          clickRoot.addEventListener("wheel", onSheetWheel, { capture: true, passive: false });
          clickRoot.addEventListener("mousewheel", onSheetWheel, { capture: true, passive: false });
          window.addEventListener("mousemove", patchMouseOffset, true);
          window.addEventListener("mouseup", onWindowPointerUp, true);
          layoutObserver = new ResizeObserver(() => {
            if (cancelled) return;
            if (layoutFrame) return;
            layoutFrame = window.requestAnimationFrame(() => {
              layoutFrame = 0;
              if (!cancelled) syncSheetLayout();
            });
          });
          layoutObserver.observe(previewContainerRef.current.parentElement ?? previewContainerRef.current);
          requestAnimationFrame(() => {
            if (!cancelled) syncSheetLayout();
          });
        }

        setIsLoading(false);
        onLoadSuccessRef.current?.();
      } catch (error) {
        if (cancelled || (error instanceof DOMException && error.name === "AbortError")) return;
        console.error("Excel spreadsheet load error:", error);
        const errorMessage = error instanceof Error ? error.message : "Failed to load Excel spreadsheet";
        setExcelError(errorMessage);
        setIsLoading(false);
        onLoadErrorRef.current?.(error instanceof Error ? error : new Error(errorMessage));
      }
    };

    loadWorkbook();

    return () => {
      cancelled = true;
      abortController.abort();
      if (sizeWaitTimeout != null) window.clearTimeout(sizeWaitTimeout);
      if (layoutFrame) window.cancelAnimationFrame(layoutFrame);
      sizeWaitObserver?.disconnect();
      layoutObserver?.disconnect();
      clickRoot?.removeEventListener("click", preventUnsafePreviewNavigation, true);
      clickRoot?.removeEventListener("auxclick", preventUnsafePreviewNavigation, true);
      clickRoot?.removeEventListener("mousedown", onSheetPointerDown, true);
      clickRoot?.removeEventListener("mousemove", patchMouseOffset, true);
      clickRoot?.removeEventListener("wheel", onSheetWheel, true);
      clickRoot?.removeEventListener("mousewheel", onSheetWheel, true);
      window.removeEventListener("mousemove", patchMouseOffset, true);
      window.removeEventListener("mouseup", onWindowPointerUp, true);
      previewer?.destroy();
    };
  }, [fileUrl, isThumbnail]);

  if (isThumbnail) {
    return (
      <div className={cn("relative h-full w-full overflow-hidden", className)}>
        {excelError ? (
          <div className="flex size-full items-center justify-center p-1 text-muted-foreground">
            <FileSpreadsheet className="size-8 opacity-50" />
          </div>
        ) : (
          isLoading && (
            <div className="absolute inset-0 z-10 flex items-center justify-center bg-background/80">
              <Loader2 className="size-4 animate-spin" />
            </div>
          )
        )}
        <div
          ref={previewContainerRef}
          className={cn(
            "h-full w-full min-w-0 overflow-hidden",
            "swivu-excel-preview swivu-excel-preview--thumb",
            excelError && "hidden",
          )}
        />
      </div>
    );
  }

  return (
    <div className={cn("relative flex h-full min-h-0 w-full min-w-0 flex-col self-stretch overflow-hidden", className)}>
      {excelError ? (
        <div className="flex flex-col items-center justify-center py-16 text-muted-foreground">
          <FileSpreadsheet className="mb-4 size-16 opacity-50" />
          <span className="max-w-md text-center text-sm font-medium">{excelError}</span>
        </div>
      ) : (
        <>
          <div className="relative min-h-0 w-full min-w-0 flex-1 overflow-hidden">
            {isLoading && (
              <div className="absolute inset-0 z-10 flex items-center justify-center bg-background/80">
                <Loader2 className="size-4 animate-spin" />
              </div>
            )}
            <div
              ref={previewContainerRef}
              className="h-full w-full min-w-0 overflow-hidden overscroll-contain swivu-excel-preview"
            />
          </div>
          <div className="shrink-0 border-t border-border bg-background px-3 py-1.5">
            <p className="max-h-16 overflow-auto whitespace-pre-wrap break-words text-sm">
              {selectedCell ? (
                <>
                  <span className="text-muted-foreground">{selectedCell.address}:</span>
                  {selectedCell.text ? <span className="text-foreground"> {selectedCell.text}</span> : null}
                </>
              ) : (
                <span className="text-muted-foreground">Select a cell to read its full value</span>
              )}
            </p>
          </div>
        </>
      )}

      {showName && fileName && (
        <div className="flex-shrink-0 border-t border-border bg-background/95">
          <span className="block truncate px-4 py-3 text-center text-sm font-medium">{fileName}</span>
        </div>
      )}
    </div>
  );
};

export default ExcelViewer;
